// src/components/layout/Header.tsx
import React, { useState, useEffect } from 'react';
import {
  LogOut,
  User,
  Building2,
  Shield,
  Menu,
  Languages,
  Clock,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useSidebarState } from '../../hooks/useSidebarState';
import { signOut } from '../../lib/firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../../lib/firebase/config';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTeamStore } from '../../stores/teamStore';
import { roleIconMap } from './roleIconMap';
import { usePilgrimGuideStore } from '../../stores/pilgrimGuideStore';
import { Flag } from '../common/Flag';
import toast from 'react-hot-toast';

export const Header = () => {
  const { isCollapsed, isMobileOpen, toggleMobile } = useSidebarState();
  const { translations } = useLanguage();
  const { roles } = useTeamStore();
  const [userInfo, setUserInfo] = useState<{
    name: string;
    role: 'admin' | 'guide' | 'organizer';
    gender?: 'male' | 'female';
    organizerName?: string;
    roleId?: string;
    roleColor?: string;
    roleIcon?: string;
    country?: string;
  } | null>(null);
  const [currentVersion, setCurrentVersion] = useState<string>('');

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const userId = localStorage.getItem('userId');
        const userRole = localStorage.getItem('userRole') as
          | 'admin'
          | 'guide'
          | 'organizer';

        if (!userId || !userRole) return;

        if (userRole === 'guide') {
          const guideDoc = await getDoc(doc(firestore, 'Guides', userId));
          if (guideDoc.exists()) {
            const guideData = guideDoc.data();
            const organizerDoc = await getDoc(
              doc(firestore, 'Organizers', guideData.organizerId)
            );
            const organizerName = organizerDoc.exists()
              ? organizerDoc.data().name
              : undefined;
            const organizerCountry = organizerDoc.exists()
              ? organizerDoc.data().country
              : undefined;

            // Get guide's role info
            const userDoc = await getDoc(doc(firestore, 'Users', userId));
            const roleId = userDoc.exists() ? userDoc.data().roleId : undefined;
            const role = roleId ? roles[roleId] : null;

            setUserInfo({
              name: guideData.name,
              role: 'guide',
              gender: guideData.gender || 'male',
              organizerName,
              roleId,
              roleColor: role?.color || '#00897B',
              roleIcon: role?.icon,
              country: organizerCountry
            });
          }
        } else if (userRole === 'organizer') {
          const organizerDoc = await getDoc(
            doc(firestore, 'Organizers', userId)
          );
          if (organizerDoc.exists()) {
            const organizerData = organizerDoc.data();
            
            // Get organizer's role info
            const userDoc = await getDoc(doc(firestore, 'Users', userId));
            const roleId = userDoc.exists() ? userDoc.data().roleId : undefined;
            const role = roleId ? roles[roleId] : null;

            setUserInfo({
              name: organizerData.name,
              role: 'organizer',
              gender: organizerData.gender || 'male',
              roleId,
              roleColor: role?.color || '#00897B',
              roleIcon: role?.icon,
              country: organizerData.country
            });
          }
        } else {
          const userDoc = await getDoc(doc(firestore, 'Users', userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const role = userData.roleId ? roles[userData.roleId] : null;

            setUserInfo({
              name: userData.name,
              role: 'admin',
              gender: userData.gender || 'male',
              roleId: userData.roleId,
              roleColor: role?.color || '#00897B',
              roleIcon: role?.icon
            });
          }
        }
      } catch (error) {
        console.error('Error loading user info:', error);
      }
    };

    loadUserInfo();
  }, [roles]);

  // Load current version from Firestore
  useEffect(() => {
    const loadVersion = async () => {
      try {
        const versionRef = doc(firestore, 'version', 'version');
        const versionDoc = await getDoc(versionRef);
        if (versionDoc.exists()) {
          setCurrentVersion(versionDoc.data().version);
        }
      } catch (error) {
        console.error('Error loading version:', error);
      }
    };
    loadVersion();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
      localStorage.clear();
      window.location.reload();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const RoleIcon = userInfo?.roleIcon ? roleIconMap[userInfo.roleIcon] : Shield;

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="bg-white/80 backdrop-blur-lg border-b sticky top-0 z-20"
    >
      <div className="h-16 flex items-center justify-between px-6">
        {/* Mobile Menu Button */}
        {!isMobileOpen && (
          <button
            onClick={toggleMobile}
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
          >
            <Menu size={24} />
          </button>
        )}

        {/* Version Display - Mobile */}
        <div className="lg:hidden flex items-center gap-2 text-sm text-gray-500">
          <Clock size={16} />
          <span>v{currentVersion}</span>
        </div>

        {/* Company Logo & Copyright */}
        <div className="hidden lg:flex items-center gap-2">
          <img 
            src="https://i.ibb.co/jvPWN34G/rehab-logo-1-page-0001-1.png" 
            alt="Rehab Company" 
            className="h-8"
          />
          <span className="text-xs text-gray-500">© 2025 Rehab Company. All rights reserved.</span>
        </div>

        {/* Version Display - Desktop */}
        <div className="hidden lg:flex items-center gap-2 text-sm text-gray-500">
          <Clock size={16} />
          <span>v{currentVersion}</span>
        </div>

        <LanguageSwitcher />

        {userInfo && (
          <div className="flex items-center gap-4">
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-red-500 hover:text-red-600 transition-colors"
            >
              <LogOut size={18} />
              <span className="text-sm hidden sm:inline">
                {translations?.auth?.logout || 'Logout'}
              </span>
            </button>

            <div className="h-6 w-px bg-gray-200 hidden sm:block" />

            <div className="flex items-center gap-3">
              {/* Role Badge */}
              <div 
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg"
                style={{ 
                  backgroundColor: `${userInfo.roleColor}15`,
                  color: userInfo.roleColor
                }}
              >
                <RoleIcon size={16} />
                <span className="text-sm font-medium">
                  {userInfo.roleId && roles[userInfo.roleId]
                    ? roles[userInfo.roleId].name
                    : translations?.roles?.labels?.[userInfo.role] ||
                      userInfo.role}
                </span>
              </div>

              {/* Country Badge - Only if country exists */}
              {userInfo.country && (
                <div className="hidden sm:flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
                  <Flag countryCode={userInfo.country} size={16} />
                  <span className="text-sm font-medium text-gray-700">
                    {getCountryName(userInfo.country)}
                  </span>
                </div>
              )}

              {/* Organization Info (for guides) */}
              {userInfo.organizerName && (
                <div className="hidden sm:flex items-center gap-2 bg-secondary-50 text-secondary-600 px-3 py-1.5 rounded-lg">
                  <Building2 size={16} />
                  <span className="text-sm font-medium">
                    {userInfo.organizerName}
                  </span>
                  {userInfo.country && (
                    <Flag countryCode={userInfo.country} size={14} />
                  )}
                </div>
              )}

              {/* User Info */}
              <div className="flex items-center gap-2">
                <div
                  className={`p-2 rounded-lg ${userInfo.gender === 'female' ? 'bg-pink-50' : 'bg-blue-50'}`}
                >
                  <User
                    size={18}
                    className={
                      userInfo.gender === 'female'
                        ? 'text-pink-500'
                        : 'text-blue-500'
                    }
                  />
                </div>
                <span className="font-medium hidden sm:inline">{userInfo.name}</span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Mobile Copyright Footer */}
      <div className="lg:hidden px-6 py-2 bg-gray-50 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
        <img 
          src="https://i.ibb.co/jvPWN34G/rehab-logo-1-page-0001-1.png" 
          alt="Rehab Company" 
          className="h-4"
        />
        <span>© 2025 Rehab Company. All rights reserved.</span>
      </div>
    </motion.header>
  );
};

// Helper function to get country name from code
const getCountryName = (code: string): string => {
  const countries: Record<string, string> = {
    'CH': 'Switzerland',
    'FR': 'France',
    'IT': 'Italy',
    'CA': 'Canada',
    'ES': 'Spain',
    'CN': 'China',
    'GB': 'UK',
    'NL': 'Netherlands',
    'BE': 'Belgium',
    'US': 'USA',
    'DE': 'Germany',
    'SE': 'Sweden'
  };
  return countries[code] || code;
};
